import React from 'react';
import { useInput } from 'react-admin';
import TextField from '@material-ui/core/TextField';

/**
 * Time picker for the free-text time fields on the processing worksheet.
 *
 * Uses the browser's native time control, so the phlebotomist gets a real
 * picker and can still type quickly - and there is no extra dependency to
 * install. Chrome shows it in the user's locale (10:30 AM in en-US) while the
 * control itself works in 24-hour "HH:mm".
 *
 * The DB columns are plain strings that print screens render verbatim, so the
 * stored value stays in the 12-hour form the worksheets have always used
 * rather than becoming 24-hour. Existing records hold several spellings of
 * that ("10:00AM", "10.30AM", "10.25 AM"); all of them are read, and anything
 * saved from here is written back in one consistent shape.
 *
 * A value that cannot be parsed at all is never destroyed: the picker shows
 * empty, the original text is shown beside it, and saving without touching the
 * field leaves it exactly as it was.
 */

const pad = n => (n < 10 ? '0' + n : String(n));

/**
 * Stored text -> { hours, minutes } in 24-hour time, or null when it is not a
 * time we recognise. Deliberately generous about separators and spacing.
 */
export const parseTime = (raw) => {
    if (raw === null || raw === undefined) { return null; }

    let s = String(raw).trim().toLowerCase();
    if (!s) { return null; }

    // Pull the meridiem off the end: am, a.m., p m, pm.
    let meridiem = null;
    const mer = s.match(/([ap])\s*\.?\s*m?\s*\.?$/);
    if (mer) {
        meridiem = mer[1];
        s = s.slice(0, mer.index).trim();
    }

    // What's left must be digits with an optional single separator.
    const parts = s.match(/^(\d{1,2})\s*[:.h]?\s*(\d{2})?$/);
    let hours;
    let minutes;

    if (parts) {
        hours = parseInt(parts[1], 10);
        minutes = parts[2] === undefined ? 0 : parseInt(parts[2], 10);
    } else {
        // Unseparated forms: 930, 1030.
        const digits = s.match(/^(\d{3,4})$/);
        if (!digits) { return null; }
        const d = digits[1];
        hours = parseInt(d.slice(0, d.length - 2), 10);
        minutes = parseInt(d.slice(-2), 10);
    }

    if (isNaN(hours) || isNaN(minutes)) { return null; }
    if (minutes > 59) { return null; }

    if (meridiem) {
        if (hours < 1 || hours > 12) { return null; }
        if (meridiem === 'p' && hours !== 12) { hours += 12; }
        if (meridiem === 'a' && hours === 12) { hours = 0; }
    } else if (hours > 23) {
        return null;
    }

    return { hours: hours, minutes: minutes };
};

/** Stored text -> "HH:mm" for the native control, or '' when unreadable. */
export const toPickerValue = (raw) => {
    const t = parseTime(raw);
    return t ? pad(t.hours) + ':' + pad(t.minutes) : '';
};

/** "HH:mm" from the native control -> the text we store. */
export const toStoredValue = (picker) => {
    if (!picker) { return ''; }
    const t = parseTime(picker);
    if (!t) { return ''; }
    const period = t.hours >= 12 ? 'PM' : 'AM';
    let hour12 = t.hours % 12;
    if (hour12 === 0) { hour12 = 12; }
    return hour12 + ':' + pad(t.minutes) + ' ' + period;
};

const TimeInput = ({
    source,
    label,
    validate,
    disabled,
    className,
    // Match react-admin's own inputs, which route through ResettableTextField
    // and default to a dense filled field. Without these the control renders
    // as a shorter underline input and sits at a different height to every
    // other field in the row - the worksheet cells are vertical-align: top,
    // so any height difference shows up as misalignment.
    variant = 'filled',
    margin = 'dense',
    ...rest
}) => {
    const {
        input,
        meta: { touched, error },
        isRequired,
    } = useInput({ source, validate, ...rest });

    const stored = input.value;
    const picker = toPickerValue(stored);

    // Held a value we could not read - say so instead of appearing to have
    // silently emptied the field.
    const unreadable = Boolean(stored) && !picker;

    return (
        <TextField
            type="time"
            name={input.name}
            label={label}
            variant={variant}
            margin={margin}
            fullWidth
            value={picker}
            onChange={event => input.onChange(toStoredValue(event.target.value))}
            onBlur={input.onBlur}
            onFocus={input.onFocus}
            disabled={disabled}
            required={isRequired}
            className={className}
            error={Boolean(touched && error)}
            helperText={
                (touched && error)
                    ? error
                    : (unreadable ? 'Was: ' + stored : undefined)
            }
            InputLabelProps={{ shrink: true }}
            // Whole minutes; without this some browsers offer a seconds box.
            inputProps={{ step: 60 }}
        />
    );
};

export default TimeInput;
